import React, { useState, useEffect } from 'react';
import { Fish, Search, TrendingUp, Users, MapPin, Star } from 'lucide-react';
import { socialService } from '../../../core/services/firebase/socialService';
import { postService } from '../../../core/services/firebase/postService';
import { SocialProfile, SocialPost } from '../../../core/types/social';
import { PostCard } from '../../social/components/PostCard';

export const SocialExplorePage: React.FC = () => {
  const [trendingPosts, setTrendingPosts] = useState<SocialPost[]>([]);
  const [activeTab, setActiveTab] = useState<'posts' | 'people' | 'locations'>('posts');
  const [loading, setLoading] = useState(true);
  const [authorProfiles, setAuthorProfiles] = useState<Record<string, SocialProfile>>({});

  useEffect(() => {
    const loadExploreData = async () => {
      setLoading(true);
      try {
        const posts = await postService.getFeedPosts(20);
        setTrendingPosts(posts);
        
        // Load author profiles
        const uniqueAuthors = [...new Set(posts.map(p => p.authorId))];
        const profilesMap: Record<string, SocialProfile> = {};
        
        await Promise.all(
          uniqueAuthors.map(async (authorId) => {
            const profile = await socialService.getProfileByHandle(authorId);
            if (profile) profilesMap[authorId] = profile;
          })
        );
        
        setAuthorProfiles(profilesMap);
      } catch (error) {
        console.error('Error loading explore data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadExploreData();
  }, []);

  return (
    <div className="min-h-screen bg-brand-midnight">
      <main className="pt-16 pb-16">
        {/* Hero Search Section */}
        <section className="bg-white/5 border-b border-white/10 py-16 text-white mb-12 relative overflow-hidden">
          <div className="absolute inset-0 bg-glow-mesh-dark opacity-10"></div>
          <div className="container mx-auto px-6 text-center relative z-10">
            <h1 className="text-4xl md:text-5xl font-black mb-6 tracking-tight font-display uppercase">Explorar a Comunidade</h1>
            <p className="text-xl text-white/40 mb-8 max-w-2xl mx-auto font-medium">
              Descubra as melhores técnicas, locais secretos e conecte-se com os maiores pescadores do Brasil.
            </p>
            <div className="max-w-xl mx-auto relative group">
              <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-brand-gold transition-colors" size={24} />
              <input 
                type="text" 
                placeholder="Buscar por #tucunaré, local ou técnica..."
                className="w-full pl-16 pr-8 py-5 rounded-2xl bg-white/5 text-white border border-white/10 focus:ring-4 focus:ring-brand-gold/10 transition-all outline-none text-lg font-medium placeholder:text-white/20"
              />
            </div>
          </div>
        </section>

        <div className="container mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Main Feed Column */}
          <div className="lg:col-span-8">
            <div className="flex items-center gap-4 mb-8 overflow-x-auto pb-2 scrollbar-hide">
              <button 
                onClick={() => setActiveTab('posts')}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest transition-all whitespace-nowrap border ${
                  activeTab === 'posts' ? 'bg-brand-gold text-brand-midnight border-brand-gold shadow-glow-gold' : 'bg-white/5 text-white/40 border-white/10 hover:bg-white/10'
                }`}
              >
                <TrendingUp size={14} /> Tendências
              </button>
              <button 
                onClick={() => setActiveTab('people')}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest transition-all whitespace-nowrap border ${
                  activeTab === 'people' ? 'bg-brand-gold text-brand-midnight border-brand-gold shadow-glow-gold' : 'bg-white/5 text-white/40 border-white/10 hover:bg-white/10'
                }`}
              >
                <Users size={14} /> Pescadores
              </button>
              <button 
                onClick={() => setActiveTab('locations')}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest transition-all whitespace-nowrap border ${
                  activeTab === 'locations' ? 'bg-brand-gold text-brand-midnight border-brand-gold shadow-glow-gold' : 'bg-white/5 text-white/40 border-white/10 hover:bg-white/10'
                }`}
              >
                <MapPin size={14} /> Locais
              </button>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 gap-6">
                {[1, 2, 3].map(i => (
                  <div key={i} className="bg-white/5 h-96 rounded-3xl animate-pulse border border-white/10"></div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-8">
                {trendingPosts.map(post => (
                  <PostCard 
                    key={post.id} 
                    post={post} 
                    authorName={authorProfiles[post.authorId]?.displayName}
                    authorAvatar={authorProfiles[post.authorId]?.avatarUrl}
                    authorVerification={authorProfiles[post.authorId]?.verification}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Right Trends Sidebar */}
          <aside className="lg:col-span-4 space-y-8">
             <div className="bg-brand-charcoal/30 rounded-3xl p-8 border border-white/10">
                <h3 className="font-black text-xs uppercase tracking-[0.3em] text-white/30 mb-8 flex items-center gap-2">
                   <Star className="text-brand-gold" fill="currentColor" size={14} />
                   Tags em Alta
                </h3>
                <div className="space-y-6">
                   {[
                      { name: 'tucurare', count: '2.5k posts', icon: '🎣' },
                      { name: 'amazonia', count: '1.8k posts', icon: '🌳' },
                      { name: 'flyfishing', count: '950 posts', icon: '🦋' },
                      { name: 'robalo', count: '820 posts', icon: '🌊' },
                      { name: 'araguaia', count: '740 posts', icon: '🚣' }
                   ].map(tag => (
                       <div key={tag.name} className="flex items-center justify-between group cursor-pointer">
                          <div className="flex items-center gap-3">
                             <span className="text-lg">{tag.icon}</span>
                             <div>
                                <p className="font-bold text-white group-hover:text-brand-gold transition-colors">#{tag.name}</p>
                                <p className="text-[10px] text-white/20 font-black uppercase tracking-widest">{tag.count}</p>
                             </div>
                          </div>
                          <TrendingUp size={14} className="text-brand-gold opacity-0 group-hover:opacity-100 transition-all" />
                       </div>
                   ))}
                </div>
                 <button className="w-full mt-8 py-3 bg-white/5 text-white/40 font-black text-[10px] uppercase tracking-widest rounded-xl hover:bg-white/10 transition-colors">
                   Ver Ranking Completo
                </button>
             </div>

             <div className="bg-brand-orange/5 rounded-3xl p-8 border border-white/10 relative overflow-hidden group cursor-pointer">
                <div className="relative z-10">
                   <h3 className="font-black text-xs uppercase tracking-widest text-brand-orange mb-2">Conheça o Marketplace</h3>
                   <p className="text-sm text-white/40 mb-6 font-medium">Equipamentos testados por profissionais e pacotes exclusivos.</p>
                   <button className="bg-brand-orange text-white px-6 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest shadow-glow-orange hover:scale-105 transition-all">
                      Explorar Lojas
                   </button>
                </div>
                <Fish className="absolute -bottom-6 -right-6 text-brand-orange/5 rotate-12 scale-150 group-hover:rotate-45 transition-transform duration-700" size={160} />
             </div>
          </aside>
        </div>
      </main>
    </div>
  );
};
